import React, { useCallback, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { smLight } from '../theme/typography';
import { themeColor } from '../theme/colors';
import {
  CellContext,
  Header,
  Table as ReactTable,
  RowData,
  flexRender,
} from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import { TableHeader } from './TableHeader';
import { CopyButton } from '../CopyButton/CopyButton';
import { CopyMenu } from '../CopyMenu/CopyMenu';
import Skeleton from '../Skeleton/Skeleton';
import Menu from '../Menu/Menu';
import { HudIconProps } from '../Icons/HudIcons';

import { EmptyStateProps, TableEmptyState } from './TableEmptyState';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    copyable?: boolean;
    // copyableValues?: Record<string, (row: TData) => string>;
    copyableItems?: {
      icon?: HudIconProps['icon'];
      leftSection?: React.ReactNode;
      label: string;
      accessor: (row: TData) => string;
    }[];
    narrow?: boolean;
    fitFormatter?: (cell: CellContext<TData, TValue>) => string;
    disableAutoFit?: boolean;
    ignoreSize?: boolean;
    rightAlign?: boolean;
    description?: string;
    topAlign?: boolean;
  }
}

export const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;
  background: ${themeColor('backgroundLight')};

  --padding-left-regular: 20px;
  --padding-left-narrow: 10px;
  padding-left: var(--padding-left-regular);
`;

export const TableHead = styled.thead``;

export const TableBody = styled.tbody``;

export const CopyMenuContainer = styled.div`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
`;

export const TableRow = styled.tr`
  &:nth-child(even) {
    background: ${themeColor('tableRowAlternateColor')};
  }

  ${CopyMenuContainer} {
    opacity: 0;

    transition: opacity 0.1s ease-in-out;

    &:has(${Menu.Menu}[data-expanded='true']) {
      opacity: 1;
    }
  }

  &:hover {
    background-color: ${themeColor('tableHeaderBackground')};
  }

  & th:not(:last-of-type),
  & td:not(:last-of-type) {
    border-right: 1px solid ${themeColor('strokeLight')};
  }

  & td:hover ${CopyMenuContainer} {
    opacity: 1;
  }
`;

export const TableCell = styled.td<{
  $narrow?: boolean;
  $rightAlign?: boolean;
  $topAlign?: boolean;
}>`
  ${smLight}
  line-height: inherit;
  color: ${themeColor('textPrimary')};
  height: 36px;

  padding-top: 6px;
  padding-bottom: 6px;
  padding-right: 20px;
  color: var(--color-red);
  width: fit-content;
  word-break: break-all;
  position: relative;

  ${(props) =>
    props.$narrow
      ? css`
          padding-left: var(--padding-left-narrow);
          padding-right: var(--padding-left-narrow);
        `
      : css`
          padding-left: var(--padding-left-regular);
        `}

  ${(props) =>
    props.$rightAlign
      ? css`
          text-align: right;
          text-align: -webkit-right;
        `
      : css``}

  ${(props) =>
    props.$topAlign &&
    css`
      padding-top: 10px;
      vertical-align: top;
    `}
`;

export const LinkCell = styled(Link)`
  color: ${themeColor('textPrimary')};
  text-decoration: underline;
  word-break: break-word;
`;

export const CopyHeader = styled.th`
  user-select: none;
  background: ${themeColor({
    dark: 'backgroundLight',
    light: 'backgroundDark',
  })};
  color: ${themeColor('textTertiary')};
  border-collapse: collapse;
  width: 18px;
  padding: 0;
`;

export interface DefaultTableProps<T> {
  table: ReactTable<T>;
  className?: string;
  label?: string;
  loading?: boolean;
  loadingRows?: number;

  options?: {
    hideEmptyState?: boolean;
    emptyStateProps?: EmptyStateProps;
  };
}

const DEFAULT_AUTOFIT_BUFFER = 30;
export function DefaultTable<T>({
  table,
  className,
  label,
  loading,
  loadingRows = 20,
  options = {},
  ...rest
}: DefaultTableProps<T>) {
  const calculateColumnSize = (
    header: Header<T, unknown>,
    values: string[],
  ) => {
    const minSize = header.column.columnDef.minSize ?? -999;
    const maxSize = header.column.columnDef.maxSize;
    const longestValue = values.reduce(
      (acc, value) => (value.length > acc.length ? value : acc),
      '',
    );
    const temp = document.createElement('span');
    temp.innerHTML = longestValue;
    document.body.appendChild(temp);
    const baseSize = temp.offsetWidth + DEFAULT_AUTOFIT_BUFFER;
    document.body.removeChild(temp);
    return Math.min(maxSize ?? baseSize, Math.max(minSize, baseSize));
  };

  const handleAutoFitClicked = useCallback(
    (header: Header<T, unknown>) => {
      if (loading) return;
      const values = table.getRowModel().rows.map((row) => {
        const cell = row
          .getAllCells()
          .find((cell) => cell.column.id === header.id);
        return cell
          ? cell.column.columnDef.meta?.fitFormatter
            ? (header.column.columnDef.meta?.fitFormatter?.(
                cell.getContext(),
              ) as string)
            : (cell.getValue() as string)
          : '';
      });
      values.push(
        (typeof header.column.columnDef.header === 'string'
          ? header.column.columnDef.header
          : ('' as string)) + 'XX',
      ); // add padding because of the sort indicator

      const newSize = calculateColumnSize(header, values);
      const currentColumnsSizing = table.getFlatHeaders().reduce(
        (acc, header) => {
          acc[header.id] = header.getSize();
          return acc;
        },
        {} as Record<string, number>,
      );

      table.setColumnSizing({
        ...currentColumnsSizing,
        [header.id]: newSize,
      });
    },
    [table, loading],
  );

  useEffect(() => {
    const columnToSize: Record<string, number> = {};
    table.getFlatHeaders().forEach((header) => {
      if (header.column.columnDef.meta?.disableAutoFit) return;
      let size = header.getSize();
      if (!loading) {
        const values = table.getRowModel().rows.map((row) => {
          const cell = row
            .getAllCells()
            .find((cell) => cell.column.id === header.id);
          return cell
            ? cell.column.columnDef.meta?.fitFormatter
              ? (header.column.columnDef.meta?.fitFormatter?.(
                  cell.getContext(),
                ) as string)
              : String(cell.getValue())
            : '';
        });
        values.push(
          (typeof header.column.columnDef.header === 'string'
            ? header.column.columnDef.header
            : ('' as string)) + 'XX',
        ); // add padding because of the sort indicator

        size = calculateColumnSize(header, values);
      }

      if (size !== header.getSize()) {
        columnToSize[header.id] = size;
      }
    });

    if (Object.keys(columnToSize).length > 0) {
      const currentColumnsSizing = table.getFlatHeaders().reduce(
        (acc, header) => {
          acc[header.id] = header.getSize();
          return acc;
        },
        {} as Record<string, number>,
      );
      table.setColumnSizing({
        ...currentColumnsSizing,
        ...columnToSize,
      });
    }
  }, [table.options.data, loading]);

  const showEmptyState = !loading && table.getRowModel().rows.length === 0 && !options?.hideEmptyState;

  return (
    <>
      <Table className={className} aria-label={label} {...rest}>
        <TableHead>
          <TableRow>
            {table.getFlatHeaders().map((header) => (
              <React.Fragment key={header.id}>
                <TableHeader
                  sortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  sortDirection={header.column.getIsSorted()}
                  resizable={table && header.column.getCanResize()}
                  width={
                    header.column.columnDef.meta?.ignoreSize
                      ? undefined
                      : header.getSize()
                  }
                  narrow={header.column.columnDef.meta?.narrow}
                  resizeHandler={header.getResizeHandler()}
                  isResizing={header.column.getIsResizing()}
                  onAutoFitClick={() => {
                    handleAutoFitClicked(header);
                  }}
                  description={header.column.columnDef.meta?.description}
                  aria-label={
                    typeof header.column.columnDef.header === 'string'
                      ? header.column.columnDef.header
                      : header.id
                  }
                  aria-sort={
                    header.column.getIsSorted() === false
                      ? undefined
                      : header.column.getIsSorted() === 'asc'
                        ? 'ascending'
                        : 'descending'
                  }
                  role="columnheader"
                  aria-colindex={header.index + 1}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHeader>
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? Array.from({ length: loadingRows }).map((_, index) => (
                <TableRow key={index}>
                  {table.getFlatHeaders().map((header) => (
                    <TableCell key={header.id}>
                      <Skeleton width="100%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <React.Fragment key={cell.id}>
                      <TableCell
                        role="cell"
                        aria-label={
                          typeof cell.column.columnDef.header === 'string'
                            ? cell.column.columnDef.header
                            : cell.column.id
                        }
                        $rightAlign={
                          cell.column.columnDef.meta?.rightAlign ?? false
                        }
                        $narrow={cell.column.columnDef.meta?.narrow}
                        $topAlign={cell.column.columnDef.meta?.topAlign}
                        style={{
                          width:
                            !cell.column.columnDef.meta?.ignoreSize &&
                            cell.column.getSize()
                              ? `${cell.column.getSize()}px`
                              : undefined,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                        {cell.column.columnDef.meta?.copyable && (
                          <CopyMenuContainer>
                            {cell.column.columnDef.meta.copyableItems ? (
                              <CopyMenu
                                copyableItems={cell.column.columnDef.meta.copyableItems.map(
                                  ({ label, icon, leftSection, accessor }) => ({
                                    label,
                                    icon,
                                    leftSection,
                                    value: accessor(row.original as T),
                                  }),
                                )}
                              />
                            ) : (
                              <CopyButton value={cell.getValue() as string} />
                            )}
                          </CopyMenuContainer>
                        )}
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              ))}
        </TableBody>
      </Table>
      {showEmptyState && <TableEmptyState {...options?.emptyStateProps} />}
    </>
  );
}
